import { hasPermission } from '@ctrl/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Percent } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { CheckboxField, SelectField, SwitchField, TextField } from '@/components/form-fields';
import { useDetailTitle } from '@/components/page-header';
import {
  AddButton,
  EmptyState,
  ListRow,
  ListShell,
  ReadOnlyNotice,
  SectionCard,
} from '@/components/settings/settings-kit';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth';
import {
  addEligibility,
  addSchedule,
  addTarget,
  type CustomerType,
  type DiscountRecord,
  getDiscount,
  listEligibility,
  listSchedules,
  listTargets,
  removeEligibility,
  removeSchedule,
  removeTarget,
  SUPPORTED_DISCOUNT_KINDS,
  type TargetType,
  updateDiscount,
} from '@/lib/retention';
import { DAY_NAMES } from '@/lib/settings/locations';
import { useForm } from '@/lib/use-form';

/**
 * One discount.
 *
 * The pricing pipeline resolves a promo by code, checks its window, its
 * limits and its eligibility, and applies percentage or fixed amount. Bogo
 * and bundle exist in the enum and nowhere in `resolvePromo`, so this editor
 * does not offer them: a saved rule that silently discounts nothing is worse
 * than an absent feature.
 */
export const Route = createFileRoute('/discounts/$discountId')({
  component: DiscountDetailPage,
});

const schema = z.object({
  name: z.string().trim().min(1, 'Name the discount').max(120),
  code: z
    .string()
    .trim()
    .max(40)
    .regex(/^[A-Za-z0-9-]*$/, 'Letters, numbers and dashes'),
  kind: z.enum(['percentage', 'fixed_amount']),
  value: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,2})?$/, 'A number, e.g. 10'),
  minSubtotal: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,2})?$/, 'Amounts look like 20.00'),
  maxDiscount: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,2})?$/, 'Amounts look like 25.00')
    .or(z.literal('')),
  usageLimit: z.string().trim().regex(/^\d*$/, 'A whole number'),
  usageLimitPerCustomer: z.string().trim().regex(/^\d*$/, 'A whole number'),
  stackable: z.boolean(),
  stackingPriority: z
    .string()
    .trim()
    .regex(/^\d{1,3}$/, 'A whole number'),
  startsAt: z.string().trim(),
  endsAt: z.string().trim(),
  channels: z.array(z.string()),
  fulfillment: z.array(z.string()),
  active: z.boolean(),
});

type Values = z.infer<typeof schema>;

const CHANNELS = ['web', 'pos', 'kiosk', 'marketplace'];
const FULFILLMENT = ['pickup', 'delivery', 'curbside', 'walk_in', 'kiosk'];

/** `2026-08-30T12:00:00Z` -> what a `datetime-local` input wants. */
const toLocalInput = (value: string | null) => (value ? value.slice(0, 16) : '');
const fromLocalInput = (value: string) => (value ? new Date(value).toISOString() : null);

function toValues(discount: DiscountRecord): Values {
  return {
    name: discount.name,
    code: discount.code ?? '',
    kind: (SUPPORTED_DISCOUNT_KINDS.includes(discount.kind)
      ? discount.kind
      : 'percentage') as Values['kind'],
    // Basis points for a percentage, cents for an amount: hundredths either way.
    value: (discount.value / 100).toFixed(2).replace(/\.00$/, ''),
    minSubtotal: (discount.minSubtotalCents / 100).toFixed(2),
    maxDiscount:
      discount.maxDiscountCents == null ? '' : (discount.maxDiscountCents / 100).toFixed(2),
    usageLimit: discount.usageLimit == null ? '' : String(discount.usageLimit),
    usageLimitPerCustomer:
      discount.usageLimitPerCustomer == null ? '' : String(discount.usageLimitPerCustomer),
    stackable: discount.stackable,
    stackingPriority: String(discount.stackingPriority),
    startsAt: toLocalInput(discount.startsAt),
    endsAt: toLocalInput(discount.endsAt),
    channels: discount.allowedChannels ?? [],
    fulfillment: discount.allowedFulfillment ?? [],
    active: discount.active,
  };
}

function DiscountDetailPage() {
  const { discountId } = useParams({ from: '/discounts/$discountId' });
  const { activeAccountId, claims } = useAuth();
  const accountId = activeAccountId ?? '';

  const canManage = accountId ? hasPermission(claims, accountId, 'discounts.manage') : false;

  const discount = useQuery({
    queryKey: ['discount', accountId, discountId],
    queryFn: () => getDiscount(accountId, discountId),
    enabled: Boolean(accountId),
  });

  useDetailTitle(discount.data?.name ?? `Discount ${discountId.slice(0, 8)}`);

  if (discount.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!discount.data) {
    return (
      <div className="text-muted-foreground flex flex-col items-center gap-3 py-24 text-center">
        <Percent className="size-8" />
        <p className="text-foreground text-sm font-medium">No such discount</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/discounts">
            <ArrowLeft className="size-4" />
            Back to discounts
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{discount.data.name}</h1>
        <p className="text-muted-foreground text-sm">
          Used {discount.data.usedCount} time{discount.data.usedCount === 1 ? '' : 's'}
        </p>
      </div>

      {!canManage && <ReadOnlyNotice permission="discounts.manage" />}

      {/*
        Mounted with the record in hand rather than reset into afterwards.
        A Radix Select whose controlled value changes to one whose item is
        not currently mounted answers by clearing itself -- which silently
        emptied `kind` and made every save fail validation with nothing on
        screen to explain it.
      */}
      <DiscountForm
        accountId={accountId}
        discountId={discountId}
        discount={discount.data}
        canManage={canManage}
      />

      <TargetsPanel accountId={accountId} discountId={discountId} canManage={canManage} />
      <EligibilityPanel accountId={accountId} discountId={discountId} canManage={canManage} />
      <SchedulePanel accountId={accountId} discountId={discountId} canManage={canManage} />
    </div>
  );
}

function DiscountForm({
  accountId,
  discountId,
  discount,
  canManage,
}: {
  accountId: string;
  discountId: string;
  discount: DiscountRecord;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const { values, errors, isDirty, set, reset, submit } = useForm<Values>(
    toValues(discount),
    schema,
  );

  const save = useMutation({
    mutationFn: (parsed: Values) =>
      updateDiscount(accountId, discountId, {
        name: parsed.name,
        code: parsed.code || null,
        kind: parsed.kind,
        value: Math.round(Number(parsed.value) * 100),
        minSubtotalCents: Math.round(Number(parsed.minSubtotal) * 100),
        maxDiscountCents:
          parsed.maxDiscount === '' ? null : Math.round(Number(parsed.maxDiscount) * 100),
        usageLimit: parsed.usageLimit === '' ? null : Number(parsed.usageLimit),
        usageLimitPerCustomer:
          parsed.usageLimitPerCustomer === '' ? null : Number(parsed.usageLimitPerCustomer),
        stackable: parsed.stackable,
        stackingPriority: Number(parsed.stackingPriority),
        allowedPaymentProviders: null,
        allowedChannels: parsed.channels.length
          ? (parsed.channels as DiscountRecord['allowedChannels'])
          : null,
        allowedFulfillment: parsed.fulfillment.length
          ? (parsed.fulfillment as DiscountRecord['allowedFulfillment'])
          : null,
        startsAt: fromLocalInput(parsed.startsAt),
        endsAt: fromLocalInput(parsed.endsAt),
        active: parsed.active,
      }),
    onSuccess: (_, parsed) => {
      toast.success('Discount saved.');
      reset(parsed);
      queryClient.invalidateQueries({ queryKey: ['discount', accountId, discountId] });
      queryClient.invalidateQueries({ queryKey: ['discounts', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <form onSubmit={submit((parsed) => save.mutate(parsed))} noValidate className="space-y-6">
      <fieldset disabled={!canManage} className="space-y-6">
        <SectionCard
          title="What it takes off"
          description="A code the shopper types, or leave it blank for a promotion that applies itself."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="discount-name"
              label="Name"
              description="What staff and the receipt call it."
              value={values.name}
              error={errors.name}
              onChange={(value) => set('name', value)}
            />
            <TextField
              id="discount-code"
              label="Code"
              description="Blank means automatic."
              placeholder="WELCOME10"
              value={values.code}
              error={errors.code}
              onChange={(value) => set('code', value.toUpperCase())}
            />
            <SelectField
              id="discount-kind"
              label="Kind"
              value={values.kind}
              options={[
                { value: 'percentage', label: 'Percentage off' },
                { value: 'fixed_amount', label: 'Fixed amount off' },
              ]}
              onChange={(value) => set('kind', value as Values['kind'])}
            />
            <TextField
              id="discount-value"
              label={values.kind === 'percentage' ? 'Percent off' : 'Dollars off'}
              value={values.value}
              error={errors.value}
              onChange={(value) => set('value', value)}
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Limits"
          description="Checked when the code is applied and again when the order is placed."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="discount-min"
              label="Minimum subtotal"
              description="Dollars. Zero means no minimum."
              value={values.minSubtotal}
              error={errors.minSubtotal}
              onChange={(value) => set('minSubtotal', value)}
            />
            <TextField
              id="discount-max"
              label="Most it can take off"
              description="Dollars. Blank for no cap."
              value={values.maxDiscount}
              error={errors.maxDiscount}
              onChange={(value) => set('maxDiscount', value)}
            />
            <TextField
              id="discount-uses"
              label="Total uses"
              description="Blank for unlimited."
              value={values.usageLimit}
              error={errors.usageLimit}
              onChange={(value) => set('usageLimit', value)}
            />
            <TextField
              id="discount-uses-customer"
              label="Uses per customer"
              description="Blank for unlimited."
              value={values.usageLimitPerCustomer}
              error={errors.usageLimitPerCustomer}
              onChange={(value) => set('usageLimitPerCustomer', value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="discount-starts">Starts</FieldLabel>
              <Input
                id="discount-starts"
                type="datetime-local"
                value={values.startsAt}
                onChange={(event) => set('startsAt', event.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="discount-ends">Ends</FieldLabel>
              <Input
                id="discount-ends"
                type="datetime-local"
                value={values.endsAt}
                onChange={(event) => set('endsAt', event.target.value)}
              />
            </Field>
          </div>
        </SectionCard>

        <SectionCard
          title="Where it applies"
          description="Nothing ticked means everywhere. Ticking narrows it."
        >
          <div>
            <p className="mb-2 text-sm font-medium">Channels</p>
            <div className="flex flex-wrap gap-4">
              {CHANNELS.map((channel) => (
                <CheckboxField
                  key={channel}
                  id={`channel-${channel}`}
                  label={channel}
                  checked={values.channels.includes(channel)}
                  onChange={(checked) =>
                    set('channels', (current) =>
                      checked
                        ? [...current, channel]
                        : current.filter((value) => value !== channel),
                    )
                  }
                />
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium">Fulfillment</p>
            <div className="flex flex-wrap gap-4">
              {FULFILLMENT.map((type) => (
                <CheckboxField
                  key={type}
                  id={`fulfillment-${type}`}
                  label={type.replace(/_/g, ' ')}
                  checked={values.fulfillment.includes(type)}
                  onChange={(checked) =>
                    set('fulfillment', (current) =>
                      checked ? [...current, type] : current.filter((value) => value !== type),
                    )
                  }
                />
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-6">
            <SwitchField
              id="discount-stackable"
              label="Stacks with other discounts"
              checked={values.stackable}
              onChange={(checked) => set('stackable', checked)}
            />
            <TextField
              id="discount-priority"
              label="Priority"
              description="Lower applies first."
              className="w-32"
              value={values.stackingPriority}
              error={errors.stackingPriority}
              onChange={(value) => set('stackingPriority', value)}
            />
            <SwitchField
              id="discount-active"
              label="Live"
              checked={values.active}
              onChange={(checked) => set('active', checked)}
            />
          </div>
        </SectionCard>

        <div className="flex gap-2">
          <Button type="submit" disabled={!isDirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save discount'}
          </Button>
          {isDirty && (
            <Button type="button" variant="ghost" onClick={() => reset()}>
              Discard
            </Button>
          )}
        </div>
      </fieldset>
    </form>
  );
}

function TargetsPanel({
  accountId,
  discountId,
  canManage,
}: {
  accountId: string;
  discountId: string;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({ targetType: 'category' as TargetType, targetId: '' });

  const targets = useQuery({
    queryKey: ['discount-targets', accountId, discountId],
    queryFn: () => listTargets(accountId, discountId),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['discount-targets', accountId, discountId] });

  const add = useMutation({
    mutationFn: () =>
      addTarget(accountId, {
        discountId,
        targetType: draft.targetType,
        targetId: draft.targetId.trim(),
      }),
    onSuccess: () => {
      setDraft((current) => ({ ...current, targetId: '' }));
      toast.success('Target added.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (id: number) => removeTarget(accountId, id),
    onSuccess: refresh,
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <SectionCard
      title="What it applies to"
      description="Nothing listed means the whole basket. Listing a category or brand narrows it to those lines."
    >
      {targets.data?.length ? (
        <ListShell>
          {targets.data.map((target) => (
            <ListRow
              key={target.id}
              title={target.targetType}
              subtitle={target.targetId}
              actions={
                canManage ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => remove.mutate(target.id)}
                  >
                    Remove
                  </Button>
                ) : null
              }
            />
          ))}
        </ListShell>
      ) : (
        <EmptyState>Applies to the whole basket.</EmptyState>
      )}

      {canManage && (
        <div className="flex flex-wrap items-end gap-3">
          <SelectField
            id="target-type"
            label="Kind"
            className="w-40"
            value={draft.targetType}
            options={[
              { value: 'category', label: 'Category' },
              { value: 'brand', label: 'Brand' },
              { value: 'product', label: 'Product' },
              { value: 'collection', label: 'Collection' },
            ]}
            onChange={(value) =>
              setDraft((current) => ({ ...current, targetType: value as TargetType }))
            }
          />
          <Field className="flex-1">
            <FieldLabel htmlFor="target-id">Id</FieldLabel>
            <Input
              id="target-id"
              value={draft.targetId}
              placeholder="Paste the record's id"
              onChange={(event) =>
                setDraft((current) => ({ ...current, targetId: event.target.value }))
              }
            />
          </Field>
          <AddButton onClick={() => add.mutate()} disabled={!draft.targetId.trim()}>
            Add
          </AddButton>
        </div>
      )}
    </SectionCard>
  );
}

function EligibilityPanel({
  accountId,
  discountId,
  canManage,
}: {
  accountId: string;
  discountId: string;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [customerType, setCustomerType] = useState<CustomerType>('medical');

  const rules = useQuery({
    queryKey: ['discount-eligibility', accountId, discountId],
    queryFn: () => listEligibility(accountId, discountId),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['discount-eligibility', accountId, discountId] });

  const add = useMutation({
    mutationFn: () =>
      addEligibility(accountId, {
        discountId,
        eligibilityType: 'customer_type',
        customerType,
      }),
    onSuccess: () => {
      toast.success('Rule added.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (id: number) => removeEligibility(accountId, id),
    onSuccess: refresh,
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <SectionCard
      title="Who can use it"
      description="Nothing listed means anybody. A customer type restricts it to that tier — patients only, say."
    >
      {rules.data?.length ? (
        <ListShell>
          {rules.data.map((rule) => (
            <ListRow
              key={rule.id}
              title={rule.eligibilityType.replace(/_/g, ' ')}
              subtitle={rule.customerType ?? rule.targetId ?? undefined}
              actions={
                canManage ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => remove.mutate(rule.id)}
                  >
                    Remove
                  </Button>
                ) : null
              }
            />
          ))}
        </ListShell>
      ) : (
        <EmptyState>Anybody can use it.</EmptyState>
      )}

      {canManage && (
        <div className="flex flex-wrap items-end gap-3">
          <SelectField
            id="eligibility-type"
            label="Customer type"
            className="w-48"
            value={customerType}
            options={[
              { value: 'recreational', label: 'Recreational' },
              { value: 'medical', label: 'Medical' },
              { value: 'state_medical', label: 'State medical' },
            ]}
            onChange={(value) => setCustomerType(value as CustomerType)}
          />
          <AddButton onClick={() => add.mutate()}>Restrict</AddButton>
        </div>
      )}
    </SectionCard>
  );
}

function SchedulePanel({
  accountId,
  discountId,
  canManage,
}: {
  accountId: string;
  discountId: string;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({ dayOfWeek: '2', startsAt: '16:00', endsAt: '18:00' });

  const schedules = useQuery({
    queryKey: ['discount-schedules', accountId, discountId],
    queryFn: () => listSchedules(accountId, discountId),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['discount-schedules', accountId, discountId] });

  const add = useMutation({
    mutationFn: () =>
      addSchedule(accountId, {
        discountId,
        dayOfWeek: Number(draft.dayOfWeek),
        startsAt: draft.startsAt,
        endsAt: draft.endsAt,
      }),
    onSuccess: () => {
      toast.success('Window added.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (id: number) => removeSchedule(accountId, id),
    onSuccess: refresh,
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <SectionCard
      title="Happy hour"
      description="Nothing listed means it runs whenever it is live. A window restricts it to those hours."
    >
      {schedules.data?.length ? (
        <ListShell>
          {schedules.data.map((schedule) => (
            <ListRow
              key={schedule.id}
              title={`${DAY_NAMES[schedule.dayOfWeek]} · ${schedule.startsAt}–${schedule.endsAt}`}
              actions={
                canManage ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => remove.mutate(schedule.id)}
                  >
                    Remove
                  </Button>
                ) : null
              }
            />
          ))}
        </ListShell>
      ) : (
        <EmptyState>Runs any time it is live.</EmptyState>
      )}

      {canManage && (
        <div className="flex flex-wrap items-end gap-3">
          <SelectField
            id="schedule-day"
            label="Day"
            className="w-40"
            value={draft.dayOfWeek}
            options={DAY_NAMES.map((name, index) => ({ value: String(index), label: name }))}
            onChange={(value) => setDraft((current) => ({ ...current, dayOfWeek: value }))}
          />
          <Field className="w-28">
            <FieldLabel htmlFor="schedule-start">From</FieldLabel>
            <Input
              id="schedule-start"
              type="time"
              value={draft.startsAt}
              onChange={(event) =>
                setDraft((current) => ({ ...current, startsAt: event.target.value }))
              }
            />
          </Field>
          <Field className="w-28">
            <FieldLabel htmlFor="schedule-end">To</FieldLabel>
            <Input
              id="schedule-end"
              type="time"
              value={draft.endsAt}
              onChange={(event) =>
                setDraft((current) => ({ ...current, endsAt: event.target.value }))
              }
            />
          </Field>
          <AddButton onClick={() => add.mutate()}>Add</AddButton>
        </div>
      )}
    </SectionCard>
  );
}
